import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import { Box } from '@mui/material'
import { AG_GRID_THEME } from '../BulkSheet/const'
import React, { Ref, useCallback, useMemo, useRef, useState } from 'react'
import { onCellSelectionChanged } from './gridEventCallback'
import { getUrlQueryObject } from '../../../utils/urls'
import {
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  RowClassRules,
} from 'ag-grid-community'
import { processDataFromClipboard } from '../commons/AgGrid'

// 選択行の枠線（agGrid.scss の .program-management-grid）を「連続選択はひとつの枠」に
// 見せるための補助。各選択行がブロック内のどの位置か（先頭/末尾/単独）を表示順で判定し
// pm-row-selected-* クラスを付与する。DOM順に依存しない（行アニメーションを維持できる）。
const SELECTION_BLOCK_POSITION_CLASSES = [
  'pm-row-selected-single',
  'pm-row-selected-top',
  'pm-row-selected-bottom',
]

const isRowSelectedAtIndex = (api: GridApi, index: number): boolean =>
  !!api.getDisplayedRowAtIndex(index)?.isSelected()

type SelectionBlockPosition = 'single' | 'top' | 'bottom' | 'middle' | null
const selectionBlockPosition = (
  api: GridApi,
  node: IRowNode
): SelectionBlockPosition => {
  if (!node.isSelected()) return null
  const index = node.rowIndex
  if (index === null) return null
  const above = isRowSelectedAtIndex(api, index - 1)
  const below = isRowSelectedAtIndex(api, index + 1)
  if (!above && !below) return 'single'
  if (!above) return 'top'
  if (!below) return 'bottom'
  return 'middle'
}

// 行レンダリング時（スクロール/ソート/データ更新）に評価され、画面外から戻った行にも
// 正しい位置クラスを付ける。選択変更時の即時反映は refreshSelectionBlockClasses が担う。
const SELECTION_BLOCK_ROW_CLASS_RULES: RowClassRules = {
  'pm-row-selected-single': params =>
    selectionBlockPosition(params.api, params.node) === 'single',
  'pm-row-selected-top': params =>
    selectionBlockPosition(params.api, params.node) === 'top',
  'pm-row-selected-bottom': params =>
    selectionBlockPosition(params.api, params.node) === 'bottom',
}

// 表示中の行DOMの位置クラスを選択状態に合わせて直接付け替える。redrawRows と違いDOMを
// 作り直さないため、連続選択でもカクつかず、フォーカス/セル範囲にも触れない。
// 画面外の行は再レンダリング時に rowClassRules が付けるため、表示中の行のみ更新する。
const refreshSelectionBlockClasses = (api: GridApi, root: HTMLElement) => {
  api.getRenderedNodes().forEach(node => {
    if (node.id === undefined) return
    const position = selectionBlockPosition(api, node)
    // 同一行はピン留め左/中央など複数コンテナにDOMが存在するため全て更新する。
    const rowElements = root.querySelectorAll<HTMLElement>(
      `.ag-row[row-id="${node.id}"]`
    )
    rowElements.forEach(element => {
      element.classList.remove(...SELECTION_BLOCK_POSITION_CLASSES)
      if (
        position === 'single' ||
        position === 'top' ||
        position === 'bottom'
      ) {
        element.classList.add(`pm-row-selected-${position}`)
      }
    })
  })
}

type Props = Omit<AgGridReactProps, 'gridOptions'> & {
  gridRef?: Ref<AgGridReact>
  gridOptions?: GridOptions
  // テーマルート（ag-theme-balham）に付与する追加クラス。
  // 画面単位で行ホバー/選択色などをスコープして上書きするのに使う。
  // TODO(全画面共通化): 行ホバー/選択色を全画面共通仕様にしたら、スコープ用途で
  //   渡しているこの prop は不要になる可能性がある（他用途が無ければ削除）。
  rootClassName?: string
  // TODO(全画面共通化): 選択行の skyBlue 枠線（agGrid.scss の ::after）を全画面共通仕様に
  //   したら、このフラグと下の分岐を削除し常時有効化する。現状はプログラム管理画面のみ
  //   有効化するための暫定フラグ。
  selectionRowFrameEnabled?: boolean
}
export const BulkSheetView = React.forwardRef<HTMLDivElement, Props>(
  (
    { rootClassName, selectionRowFrameEnabled, gridOptions, ...props }: Props,
    ref: React.Ref<HTMLDivElement>
  ) => {
    // BulkSheetView 自身が theme root 要素を参照できるよう、転送 ref と内部 ref を併用する。
    // 選択枠の DOM クエリを、画面固有クラスではなくこのグリッドのルート配下に限定するため。
    const rootElementRef = useRef<HTMLDivElement | null>(null)
    const setRootRef = useCallback(
      (element: HTMLDivElement | null) => {
        rootElementRef.current = element
        if (typeof ref === 'function') {
          ref(element)
        } else if (ref) {
          ;(ref as React.MutableRefObject<HTMLDivElement | null>).current =
            element
        }
      },
      [ref]
    )
    // 連続選択(shift+↓等)で選択枠クラスの更新を1フレームに集約するための rAF id。
    const selectionFrameRafRef = useRef<number | null>(null)

    // TODO(全画面共通化): selectionRowFrameEnabled での分岐をやめ、常時合成にする。
    // 有効時のみ、画面側 gridOptions に共通の rowClassRules と onSelectionChanged を合成する。
    const mergedGridOptions = useMemo<GridOptions | undefined>(() => {
      if (!selectionRowFrameEnabled) return gridOptions
      const screenOnSelectionChanged = gridOptions?.onSelectionChanged
      return {
        ...gridOptions,
        rowClassRules: {
          ...SELECTION_BLOCK_ROW_CLASS_RULES,
          ...gridOptions?.rowClassRules,
        },
        onSelectionChanged: event => {
          // 選択枠クラスの更新はDOMを作り直さず行う（カクつき/フォーカス・レンジ干渉を回避）。
          if (selectionFrameRafRef.current !== null) {
            cancelAnimationFrame(selectionFrameRafRef.current)
          }
          selectionFrameRafRef.current = requestAnimationFrame(() => {
            selectionFrameRafRef.current = null
            const { api } = event
            const root = rootElementRef.current
            if (api.isDestroyed() || !root) return
            refreshSelectionBlockClasses(api, root)
          })
          screenOnSelectionChanged?.(event)
        },
      }
    }, [gridOptions, selectionRowFrameEnabled])

    const rowBuffer = useMemo(() => {
      const urlQueryString = getUrlQueryObject()
      return !isNaN(Number(urlQueryString?.rowBuffer))
        ? (urlQueryString.rowBuffer as number)
        : 10
    }, [])

    const [copyHeadersToClipboard, setCopyHeadersToClipboard] = useState(false)
    const onKeyDown = useCallback(
      e => {
        setCopyHeadersToClipboard(e.shiftKey)
      },
      [setCopyHeadersToClipboard]
    )

    const [sideBarConfig, setSideBarConfig] = useState({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: '',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressValues: true,
            suppressRowGroups: true,
          },
        },
      ],
      defaultToolPanel: '',
    })

    const onGridReady = useCallback(
      (event: GridReadyEvent) => {
        if (props.onGridReady) {
          ;(props.onGridReady as (event: GridReadyEvent) => void)(event)
        }
      },
      [props.onGridReady]
    )

    return (
      <Box
        ref={setRootRef}
        className={
          rootClassName ? `${AG_GRID_THEME} ${rootClassName}` : AG_GRID_THEME
        }
        sx={{
          width: '100%',
          height: '100%',
          padding: '0 8px 8px',
        }}
        onKeyDown={onKeyDown}
      >
        <AgGridReact
          ref={props.gridRef}
          getRowId={params => params.data.uuid}
          getDataPath={data => data.treeValue}
          rowBuffer={rowBuffer}
          animateRows={true}
          suppressGroupRowsSticky={true}
          suppressColumnVirtualisation={true}
          rowSelection={{
            mode: 'multiRow',
            checkboxes: false,
            headerCheckbox: false,
          }}
          cellSelection={true}
          undoRedoCellEditing={true}
          suppressOverlays={[
            'noRows',
            'noMatchingRows',
            'loading',
            'exporting',
          ]}
          onCellSelectionChanged={onCellSelectionChanged}
          processDataFromClipboard={processDataFromClipboard}
          copyHeadersToClipboard={copyHeadersToClipboard}
          sideBar={sideBarConfig}
          {...(props as AgGridReactProps)}
          gridOptions={mergedGridOptions}
          theme="legacy"
          onGridReady={onGridReady}
        />
      </Box>
    )
  }
)
